import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import './AboutSeleziumPage.css';
import AddToCartButton from "../components/AddToCartButton/AddToCartButton";
import BuyNowButton from "../components/BuyNowButton/BuyNowButton";
import { PUBLIC_API_URL } from '../services/api.config';

export default function AboutSeleziumPage({ userData, openCartModal }) {
    const { t, i18n } = useTranslation();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const categoryId = 'laptop-cases';
    const lang = i18n.language === 'ua' ? 'ua' : 'en';

    // Завантажуємо продукти з категорії laptop-cases
    useEffect(() => {
        let isCancelled = false; // Запобігання race conditions

        setLoading(true);
        setError(null);

        const abortController = new AbortController(); // Для скасування запиту при unmount

        fetch(`${PUBLIC_API_URL}/products/category?category=${categoryId}`, {
            signal: abortController.signal
        })
            .then(res => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                return res.text(); // Спочатку отримуємо як text для кращої обробки
            })
            .then(text => {
                if (isCancelled) return; // Не оновлюємо state якщо компонент unmounted

                try {
                    const data = JSON.parse(text);
                    // Відображаємо максимум 3 продукти
                    setProducts(Array.isArray(data) ? data.slice(0, 3) : []);
                } catch (jsonError) {
                    console.error('JSON parsing error:', jsonError, 'Response text:', text);
                    throw new Error('Invalid response format');
                }
            })
            .catch(err => {
                if (isCancelled || err.name === 'AbortError') return;
                
                console.error('Error loading products:', err);
                setError(err.message || 'Failed to load products');
            })
            .finally(() => {
                if (!isCancelled) {
                    setLoading(false);
                }
            });

        return () => {
            isCancelled = true;
            abortController.abort();
        };
    }, [categoryId]);

    // Функція для обробки швидкого перегляду (як на CategoryPage)
    const handleQuickView = (e, productId) => {
        e.preventDefault();
        // Тут можна реалізувати функціонал швидкого перегляду, якщо потрібно
    };

    // Функція для отримання найдешевшої ціни з масиву prices
    const getLowestPrice = (prices) => {
        if (!prices || !Array.isArray(prices) || prices.length === 0) {
            return 'N/A';
        }
        
        // Сортуємо ціни за зростанням для поточної мови
        const sortedPrices = [...prices].sort((a, b) => {
            const priceA = parseFloat(a.price?.[lang] || 0);
            const priceB = parseFloat(b.price?.[lang] || 0);
            return priceA - priceB;
        });
        
        // Повертаємо найдешевшу ціну
        return sortedPrices[0].price?.[lang] || 'N/A';
    };

    return (
        <div className="about-page-container">
            {/* Hero Banner */}
            <section className="about-hero-banner">
                <div className="about-hero-text">
                    <h1>{t('about.title')}</h1>
                    <p>{t('about.subtitle')}</p>
                </div>
                <div className="about-hero-image">
                    <img 
                        src="/images/about-banner.jpg" 
                        alt="Selezium Workshop"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/images/care-page-image.jpg";
                        }}
                    />
                </div>
            </section>

            {/* Our Story */}
            <section className="about-story">
                <div className="about-story-content">
                    <h2>{t('about.storyTitle')}</h2>
                    <div className="about-story-grid">
                        <div className="about-story-text">
                            <p>{t('about.storyText1')}</p>
                            <p>{t('about.storyText2')}</p>
                        </div>
                        <div className="about-story-image">
                            <img 
                                src="/images/our-story-banner.jpg"
                                alt="Leather workshop"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "/images/laptop-case/leather-texture.jpg";
                                }}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Values */}
            <section className="about-values">
                <h2>{t('about.valuesTitle')}</h2>
                <div className="about-values-grid">
                    <div className="about-value-card">
                        <div className="about-value-icon">
                            <i className="fas fa-hands"></i>
                        </div>
                        <h3>{t('about.craftsmanshipTitle')}</h3>
                        <p>{t('about.craftsmanshipText')}</p>
                    </div>

                    <div className="about-value-card">
                        <div className="about-value-icon">
                            <i className="fas fa-leaf"></i>
                        </div>
                        <h3>{t('about.sustainabilityTitle')}</h3>
                        <p>{t('about.sustainabilityText')}</p>
                    </div>

                    <div className="about-value-card">
                        <div className="about-value-icon">
                            <i className="fas fa-gem"></i>
                        </div>
                        <h3>{t('about.qualityTitle')}</h3>
                        <p>{t('about.qualityText')}</p>
                    </div>

                    <div className="about-value-card">
                        <div className="about-value-icon">
                            <i className="fas fa-heart"></i>
                        </div>
                        <h3>{t('about.communityTitle')}</h3>
                        <p>{t('about.communityText')}</p>
                    </div>
                </div>
            </section>

            {/* Our Products (replacing Collection section) */}
            <section className="about-products">
                <div className="about-products-content">
                    <h2>{t('about.productsTitle')}</h2>
                    <p>{t('about.productsText')}</p>
                    
                    {loading && (
                        <div className="about-products-loading">
                            {lang === 'ua' ? 'Завантаження продуктів...' : 'Loading products...'}
                        </div>
                    )}
                    
                    {error && (
                        <div className="about-products-error">
                            {error}
                        </div>
                    )}
                    
                    <div className="about-products-grid">
                        {products.map((product) => {
                            const imageSrc = product.mainImage
                                ? product.mainImage.replace(/^\/+/, '')
                                : '/laptop_case_example.png'; // fallback

                            // Отримуємо найдешевшу ціну з масиву prices
                            const priceLabel = getLowestPrice(product.prices);

                            // Отримуємо параметри для найдешевшої ціни
                            let selectedParams = {};
                            if (product.prices && product.prices.length > 0) {
                                // Сортуємо ціни за зростанням
                                const sortedPrices = [...product.prices].sort((a, b) => {
                                    const priceA = parseFloat(a.price?.[lang] || 0);
                                    const priceB = parseFloat(b.price?.[lang] || 0);
                                    return priceA - priceB;
                                });
                                
                                // Беремо параметри з найдешевшої ціни
                                const cheapestPrice = sortedPrices[0];
                                const { price, ...params } = cheapestPrice;
                                selectedParams = params;
                            }

                            const titleLabel = product.title?.[lang] || 'Untitled';
                            
                            // Імітація знижки для деяких товарів
                            const hasDiscount = product.ProductId % 3 === 0;
                            const oldPrice = hasDiscount ? Math.round(parseFloat(priceLabel) * 1.2) : null;
                            const discountPercent = hasDiscount ? '-20%' : null;
                            
                            // Визначаємо "новинку" на основі ID (подібно до CategoryPage)
                            const hasBadge = product.ProductId % 4 === 0;
                            const badgeLabel = hasBadge ? (lang === 'ua' ? 'Новинка' : 'New') : null;

                            return (
                                <div key={product.ProductId} className="case-card">
                                    {hasBadge && <div className="case-badge">{badgeLabel}</div>}
                                    
                                    <Link to={`/products/${product.ProductId}`} className="case-image">
                                        <img src={imageSrc} alt={titleLabel} />
                                        <div className="quick-view" onClick={(e) => handleQuickView(e, product.ProductId)}>
                                            {lang === 'ua' ? 'Швидкий перегляд' : 'Quick View'}
                                        </div>
                                    </Link>
                                    
                                    <div className="case-info">
                                        <h2 className="case-title">{titleLabel}</h2>
                                        
                                        <p className="case-price">
                                            {hasDiscount && (
                                                <>
                                                    <span className="case-price-old">
                                                        {lang === 'ua' ? `${oldPrice} грн` : `$${oldPrice}`}
                                                    </span>
                                                    <span className="case-price-discount">{discountPercent}</span>
                                                </>
                                            )}
                                            <span>
                                                {lang === 'ua' ? `${priceLabel} грн` : `$${priceLabel}`}
                                            </span>
                                        </p>

                                        <div className="case-actions">
                                            <AddToCartButton
                                                productId={product.ProductId}
                                                priceParams={selectedParams}
                                                userData={userData}
                                                openCartModal={openCartModal}
                                            >
                                                {lang === 'ua' ? 'Додати в кошик' : 'Add to Cart'}
                                            </AddToCartButton>
                                            <BuyNowButton
                                                productId={product.ProductId}
                                                priceParams={selectedParams}
                                                userData={userData}
                                            >
                                                {lang === 'ua' ? 'Купити зараз' : 'Buy Now'}
                                            </BuyNowButton>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    
                    <Link to={`/category/${categoryId}`} className="about-view-all-btn">
                        {lang === 'ua' ? 'Переглянути всі продукти' : 'View All Products'}
                    </Link>
                </div>
            </section>

            {/* Connect With Us */}
            <section className="about-connect">
                <div className="about-connect-content">
                    <h2>{t('about.connectTitle')}</h2>
                    <p>{t('about.connectText')}</p>
                    <a 
                        href="https://www.instagram.com/selezium/" 
                        className="about-instagram-btn"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <i className="fab fa-instagram"></i> @selezium
                    </a>
                </div>
            </section>
        </div>
    );
} 